import { Hourglass } from './BrandMark';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'quiet' | 'danger' | 'ink' | 'soft';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-ink',
  secondary: 'border border-line bg-paper text-ink hover:bg-paper-2',
  quiet: 'text-ink-2 hover:bg-paper-2 hover:text-ink',
  danger: 'bg-late text-white hover:opacity-90',
  /** The one strong action on a card: ink on paper, the quiet opposite of the page. */
  ink: 'bg-ink text-paper shadow-card hover:bg-ink-2',
  /** A filled, borderless secondary for cards. */
  soft: 'bg-paper-2 text-ink hover:bg-line',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
  /** `pill` rounds fully, for actions sitting on a card. */
  shape?: 'control' | 'pill';
}

/** Buttons say what happens: "Start", "Pause", "Add person". */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  shape = 'control',
  className = '',
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center gap-2 ${shape === 'pill' ? 'rounded-full' : 'rounded-control'} font-medium whitespace-nowrap transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT[variant]} ${SIZE[size]} ${block ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {loading ? <Hourglass size={16} /> : icon}
      {children}
    </button>
  );
}
